// blessings.ts
//
// Paladin buffs. Every other box on this sheet is a LIST of duties; this one is
// a GRID, and that is not a styling difference, it is the mechanic:
//
//   * A Greater Blessing is cast on a CLASS, not a person, and lasts 30 min.
//   * A paladin can hold exactly ONE blessing on a given target at a time.
//   * Different paladins stack. Four paladins means every class carries four
//     blessings; one paladin means every class carries one.
//   * A single (10 min) blessing from the SAME paladin REPLACES that paladin's
//     greater blessing on that one person. That is the only way to fix a class
//     whose members want different things, and it is what the second, smaller
//     table on a real guild sheet is for.
//
// So the sheet has two questions, and the number of paladins decides both:
// which blessings a class gets, and who casts them.
//
// The hard constraint underneath all of it: a tank must never carry Salvation.
// Tanks share a class column with DPS (warriors, feral druids, ret paladins),
// so "give the warriors Salvation" and "do not give the main tank Salvation"
// are the same decision, and it can only be resolved with a single blessing.
// That is the one thing hand-written sheets get wrong, so the patch is
// generated rather than left to be remembered.

import { Raider, RoleKey, canonClass, hasTag } from "./assignmentModel";
import { DepthOrder } from "./nightRoles";

// ── Blessings ────────────────────────────────────────────────
// The catalogue is rows in sheet_blessing, not a table in this file. Which
// blessings exist, what they are called, which spec tag grants them and how
// much each role wants them are all facts about a game version, and there is
// going to be more than one.
export type BlessingKey = string;

export type BlessingDef = {
  key: BlessingKey;
  label: string;
  short: string;
  color: string;
  greater: boolean;      // false = single target only, so 10 minutes
  tag: string;           // the spec tag that grants it
  who: string;
  weights: BlessingWeights;
};

// Scoring, as data. `requires` is a hard gate on a spec trait, `base` is the
// score per role, and `adjust` overrides one role when a trait applies. This
// is the whole of what a hardcoded wants() used to compute, and it is why
// "hunters want Might" needs no special case: it is one adjust row.
export type BlessingWeights = {
  requires?: { resource?: string; scalesWith?: string };
  base?: Partial<Record<RoleKey, number>>;
  adjust?: {
    when: { role?: RoleKey; resource?: string; scalesWith?: string };
    value: number;
  }[];
};

// A catalogue plus the lookups the rest of this module wants, built once.
export type BlessingCatalogue = {
  list: BlessingDef[];
  byKey: Record<BlessingKey, BlessingDef>;
  all: BlessingKey[];
  greater: BlessingKey[];
};

export const EMPTY_CATALOGUE: BlessingCatalogue =
  { list: [], byKey: {}, all: [], greater: [] };

export function blessingCatalogue(defs: BlessingDef[]): BlessingCatalogue {
  const byKey: Record<BlessingKey, BlessingDef> = {};
  defs.forEach((d) => { byKey[d.key] = d; });
  return {
    list: defs,
    byKey,
    all: defs.map((d) => d.key),
    // Only the greater ones go in the grid; a single-target blessing has no
    // per-class cell, and how many of them exist is what caps the grid.
    greater: defs.filter((d) => d.greater).map((d) => d.key),
  };
}

// ── Who can cast what ────────────────────────────────────────
// The database already knows. Every paladin spec carries the greater blessing
// tags it provides, and only paladin_protection carries Sanctuary, so the
// talent gate is a lookup rather than an assertion in this file.
//
// The first draft of this module guessed the gates from the spec label and got
// one wrong: it assumed a raiding retribution paladin skips Kings, and the
// game data says all three paladin specs have it.
// A paladin is whoever the game says can hand out a GREATER blessing, rather
// than whoever is called "Paladin". That is what makes this work unchanged for
// a version where the class is renamed, split or missing entirely. Single
// blessings do not count: everyone would qualify off Blessing of Light.
export const givesBlessings = (cat: BlessingCatalogue, r: Raider): boolean =>
  cat.greater.some((k) => hasTag(r, cat.byKey[k].tag));

// ── What each raider wants ───────────────────────────────────
// Reads the weights stored against each blessing rather than computing them.
// Two spec facts drive nearly all of it, and both come from the spec row:
// what resource they spend and what their output scales with.
//
// Hunters are the case that argued for storing it. They are ranged and they
// want Might, which reads as an exception worth hardcoding until you notice
// the real rule is "mana plus attack power". It is one `adjust` row now, and
// enhancement shamans and feral druids fall out of the same one.
const traitMatches = (
  r: Raider, cond: { role?: RoleKey; resource?: string; scalesWith?: string },
): boolean => {
  if (cond.role && r.role !== cond.role) return false;
  if (cond.resource && r.resource !== cond.resource) return false;
  if (cond.scalesWith && (r.scalesWith || []).indexOf(cond.scalesWith) < 0) return false;
  return true;
};

export function wants(
  cat: BlessingCatalogue, r: Raider,
): Record<BlessingKey, number> {
  const out: Record<BlessingKey, number> = {};
  cat.list.forEach((def) => {
    const w = def.weights || {};
    // A hard gate: Might does nothing for someone with no attack power, and
    // Wisdom nothing for someone with no mana bar.
    if (w.requires && !traitMatches(r, w.requires)) { out[def.key] = 0; return; }
    let value = (w.base && w.base[r.role]) || 0;
    (w.adjust || []).forEach((a) => {
      if (traitMatches(r, a.when)) value = a.value;
    });
    out[def.key] = value;
  });
  return out;
}

// ── Columns ──────────────────────────────────────────────────

export type BlessingColumn = {
  className: string;
  members: Raider[];
  tanks: Raider[];
  value: Record<BlessingKey, number>;
  ranked: BlessingKey[];   // greater blessings worth casting, best first
};

// `classOrder` is the game's own class order, so the grid reads the way the
// character screen does rather than alphabetically. Anything not in it is
// appended rather than dropped.
function buildColumns(
  cat: BlessingCatalogue, active: Raider[], classOrder: string[],
): BlessingColumn[] {
  const byClass: Record<string, Raider[]> = {};
  active.forEach((r) => {
    const c = canonClass(r.className) || "Unknown";
    (byClass[c] = byClass[c] || []).push(r);
  });

  return Object.keys(byClass).sort((a, b) => {
    const ia = classOrder.indexOf(a), ib = classOrder.indexOf(b);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.localeCompare(b);
  }).map((className) => {
    const members = byClass[className];
    const value = {} as Record<BlessingKey, number>;
    cat.all.forEach((b) => { value[b] = 0; });
    members.forEach((m) => {
      const w = wants(cat, m);
      cat.all.forEach((b) => { value[b] += w[b] || 0; });
    });
    const ranked = cat.greater
      .filter((b) => value[b] > 0)
      .sort((a, b) => value[b] - value[a] || cat.greater.indexOf(a) - cat.greater.indexOf(b));
    return {
      className, members, value, ranked,
      tanks: members.filter((m) => m.role === "tank"),
    };
  });
}

// ── Plan ─────────────────────────────────────────────────────
export type PaladinInfo = {
  raider: Raider;
  spec: string;                 // the spec's own label, or "" when unrecorded
  inferred: boolean;            // spec was guessed from class and seat
  castable: Record<BlessingKey, boolean>;
  // The blessing this paladin carries across most of the grid. Guilds run
  // paladins as lanes ("you are Kings") because re-buffing after a wipe is a
  // memory test, and one blessing per person is the only version that survives.
  lane: BlessingKey | null;
};

export type BlessingCell = {
  blessing: BlessingKey;
  source: "auto" | "manual";
  // Salvation on a column that holds a tank. Legal, correct for the DPS in it,
  // and only safe because a single blessing overwrites it on the tank.
  risky?: boolean;
};

export type SingleBlessing = {
  key: string;
  paladinId: string;
  paladinName: string;
  raider: Raider;
  blessing: BlessingKey;
  reason: string;
  source: "auto" | "manual";
  // A 30-minute buff replaced by a 10-minute one is a recurring job, not a
  // one-off, and the sheet should say so out loud.
  rebuff: boolean;
};

export type BlessingWarning = { key: string; tone: "warn" | "info"; text: string };

export type BlessingGap = {
  raider: Raider;
  blessing: BlessingKey;
  value: number;
};

export type BlessingState = {
  grid?: Record<string, string>;     // `${paladinId}::${className}` -> key | ""
  singles?: Record<string, string>;  // `${paladinId}::${characterId}` -> key | ""
  caps?: Record<string, string>;     // `${paladinId}::${blessing}` -> "1" | "0"
};

export type BlessingPlan = {
  // Carried on the plan so every consumer renders labels and colours from the
  // same rows the scoring used.
  catalogue: BlessingCatalogue;
  paladins: PaladinInfo[];
  columns: BlessingColumn[];
  grid: Record<string, BlessingCell>;
  singles: SingleBlessing[];
  warnings: BlessingWarning[];
  gaps: BlessingGap[];
  coverage: Record<string, BlessingKey[]>;   // characterId -> what they end up with
};

// Whose hands a blessing would rather be in: its own lane paladin, then one
// with no lane at all, and only then somebody already running something else.
// Within each of those, a paladin with a free cell comes first, because taking
// a busy one means bumping them and every bump is another lane broken.
const rank = (lane: BlessingKey | null, b: BlessingKey, busy: boolean) =>
  (lane === b ? 0 : lane === null ? 1 : 2) * 2 + (busy ? 1 : 0);

export const cellKey = (paladinId: string, className: string) =>
  `${paladinId}::${className}`;
export const singleKey = (paladinId: string, characterId: string) =>
  `${paladinId}::${characterId}`;

const emptyPlan = (catalogue: BlessingCatalogue): BlessingPlan => ({
  catalogue, paladins: [], columns: [], grid: {}, singles: [],
  warnings: [], gaps: [], coverage: {},
});

export const shortOf = (cat: BlessingCatalogue, k: BlessingKey): string =>
  (cat.byKey[k] && cat.byKey[k].short) || k;
export const colorOf = (cat: BlessingCatalogue, k: BlessingKey): string =>
  (cat.byKey[k] && cat.byKey[k].color) || "#94a3b8";

export function castableFor(
  cat: BlessingCatalogue, p: Raider, caps: Record<string, string>,
): Record<BlessingKey, boolean> {
  const out = {} as Record<BlessingKey, boolean>;
  cat.list.forEach((def) => {
    const set = caps[`${p.characterId}::${def.key}`];
    out[def.key] = set === undefined ? hasTag(p, def.tag) : set === "1";
  });
  return out;
}

// Paladins are ordered by the guild's own depth chart, the same ranking that
// already drives curses and misdirections. Paladin #1 takes the lane every
// column wants, so promoting a paladin moves their whole workload at once
// instead of leaving the grid half-rewritten.
function orderPaladins(
  cat: BlessingCatalogue, active: Raider[], depth: DepthOrder,
): Raider[] {
  const pals = active.filter((r) => givesBlessings(cat, r));
  // The depth chart is still keyed by class label, since that is what the
  // night sheet reorders. Anyone handing out blessings who is not in it
  // simply falls to the bottom, alphabetically.
  const rank: Record<string, number> = {};
  const chart = pals.length > 0 ? (depth[canonClass(pals[0].className)] || []) : [];
  chart.forEach((id, i) => { rank[id] = i; });
  return pals.slice().sort((a, b) => {
    const ra = rank[a.characterId], rb = rank[b.characterId];
    if (ra !== undefined && rb !== undefined) return ra - rb;
    if (ra !== undefined) return -1;
    if (rb !== undefined) return 1;
    return a.name.localeCompare(b.name);
  });
}

export function planBlessings(
  cat: BlessingCatalogue,
  roster: Raider[],
  state: BlessingState = {},
  depth: DepthOrder = {},
  classOrder: string[] = [],
): BlessingPlan {
  const active = roster.filter((r) => !r.isBench);
  const pals = orderPaladins(cat, active, depth);
  if (pals.length === 0) return emptyPlan(cat);

  const caps = state.caps || {};
  const manualGrid = state.grid || {};
  const manualSingles = state.singles || {};

  const paladins: PaladinInfo[] = pals.map((raider) => ({
    raider,
    spec: raider.specLabel || "",
    inferred: raider.traitsInferred === true,
    castable: castableFor(cat, raider, caps),
    lane: null,
  }));
  const anyCanCast = (b: BlessingKey) =>
    paladins.some((p) => p.castable[b]);

  const columns = buildColumns(cat, active, classOrder);
  const P = paladins.length;

  // 1. What each class should end up carrying: its best blessings, capped at
  //    the number of paladins, and never one nobody in the raid can cast.
  const chosen: Record<string, BlessingKey[]> = {};
  columns.forEach((c) => {
    chosen[c.className] = c.ranked.filter(anyCanCast).slice(0, P);
  });

  // 2. Lanes. Demand is counted from those lists rather than guessed, so a
  //    raid of nine casters ranks Wisdom above Might on its own.
  const demand = {} as Record<BlessingKey, number>;
  cat.greater.forEach((b) => { demand[b] = 0; });
  columns.forEach((c) => chosen[c.className].forEach((b) => { demand[b] += 1; }));

  // Lanes go to the blessings under the most pressure, not simply the most
  // wanted ones. Demand alone hands Kings to the only Protection paladin in
  // the raid and then has nobody left for Sanctuary, so the measure is demand
  // per paladin who can actually cast it: one caster for six columns outranks
  // four casters for nine. Within a blessing, the paladin highest on the depth
  // chart takes it, the same rule the rest of the sheet already follows.
  const capable = {} as Record<BlessingKey, number>;
  cat.greater.forEach((b) => {
    capable[b] = paladins.filter((p) => p.castable[b]).length;
  });
  const contention = (b: BlessingKey) =>
    capable[b] === 0 ? 0 : demand[b] / capable[b];

  cat.greater
    .filter((b) => demand[b] > 0 && capable[b] > 0)
    .sort((a, b) => contention(b) - contention(a) || demand[b] - demand[a])
    .forEach((b) => {
      const p = paladins.filter((x) => !x.lane && x.castable[b])[0];
      if (p) p.lane = b;
    });

  // 3. Fill the grid. Manual cells are claimed first so auto-fill never
  //    duplicates one, then lanes, then whatever is left over by value.
  const grid: Record<string, BlessingCell> = {};
  const givenTo: Record<string, BlessingKey[]> = {};   // characterId -> blessings

  columns.forEach((c) => {
    const want = chosen[c.className];
    const usedBlessing: Record<string, boolean> = {};
    const usedPal: Record<string, boolean> = {};

    paladins.forEach((p) => {
      const set = manualGrid[cellKey(p.raider.characterId, c.className)];
      if (set === undefined) return;
      usedPal[p.raider.characterId] = true;
      if (!set) return;                       // deliberately blank
      const b = set as BlessingKey;
      if (!cat.byKey[b] || usedBlessing[b]) return;
      usedBlessing[b] = true;
      grid[cellKey(p.raider.characterId, c.className)] = { blessing: b, source: "manual" };
    });

    // Filling the rest greedily does not work, and the failure is quiet: a
    // retribution paladin with no Kings and no Sanctuary takes the one
    // blessing they CAN cast, and the column silently ends up one short
    // because the paladin who could have covered the rest is already busy.
    // So this is a real matching. Blessings are matched in value order and
    // each one is allowed to bump an earlier match along the chain, which
    // covers as many as the talents in the raid physically allow.
    const matchPal: Record<string, BlessingKey> = {};
    // `protect` is what keeps the lanes intact. Without it the search happily
    // bumps the Kings paladin onto Might and hands Kings to somebody else,
    // which is a valid matching and a useless sheet. So the first attempt
    // refuses to disturb a paladin already sitting on their own lane, and only
    // if that fails does the second attempt allow it, which is exactly the
    // case where the lane genuinely has to move (a holy paladin cannot take
    // Sanctuary off the prot paladin's hands).
    const augment = (
      b: BlessingKey, seen: Record<string, boolean>, protect: boolean,
    ): boolean => {
      const cands = paladins
        .filter((p) => !usedPal[p.raider.characterId] && p.castable[b])
        .sort((x, y) =>
          rank(x.lane, b, !!matchPal[x.raider.characterId])
          - rank(y.lane, b, !!matchPal[y.raider.characterId]));
      for (let i = 0; i < cands.length; i++) {
        const id = cands[i].raider.characterId;
        if (seen[id]) continue;
        const held = matchPal[id];
        if (protect && held && held === cands[i].lane) continue;
        seen[id] = true;
        if (!held || augment(held, seen, protect)) {
          matchPal[id] = b;
          return true;
        }
      }
      return false;
    };
    want.filter((b) => !usedBlessing[b]).forEach((b) => {
      if (!augment(b, {}, true)) augment(b, {}, false);
    });

    paladins.forEach((p) => {
      const b = matchPal[p.raider.characterId];
      if (!b) return;
      usedBlessing[b] = true;
      usedPal[p.raider.characterId] = true;
      grid[cellKey(p.raider.characterId, c.className)] = { blessing: b, source: "auto" };
    });

    // Mark the rows that would land Salvation on a tank, and record what each
    // member now carries so the patches below know what is missing.
    paladins.forEach((p) => {
      const cell = grid[cellKey(p.raider.characterId, c.className)];
      if (!cell) return;
      if (cell.blessing === "salvation" && c.tanks.length > 0) cell.risky = true;
    });
    c.members.forEach((m) => {
      givenTo[m.characterId] = paladins
        .map((p) => grid[cellKey(p.raider.characterId, c.className)])
        .filter((cell) => !!cell)
        .map((cell) => cell.blessing);
    });
  });

  // 4. Single blessings. Two kinds are generated, and both exist because a
  //    class column is not a role: the Salvation patch, which is mandatory,
  //    and Light from a paladin who has nothing left to give that column.
  const singles: SingleBlessing[] = [];
  const singleSeen: Record<string, boolean> = {};

  const bestSingleFor = (
    target: Raider, p: PaladinInfo, exclude: BlessingKey[],
  ): BlessingKey | null => {
    const w = wants(cat, target);
    return cat.all
      .filter((b) => p.castable[b] && exclude.indexOf(b) < 0 && w[b] > 0)
      .sort((a, b) => w[b] - w[a])[0] || null;
  };

  const addSingle = (
    p: PaladinInfo, target: Raider, blessing: BlessingKey,
    reason: string, rebuff: boolean, source: "auto" | "manual",
  ) => {
    const key = singleKey(p.raider.characterId, target.characterId);
    if (singleSeen[key]) return;
    singleSeen[key] = true;
    singles.push({
      key, paladinId: p.raider.characterId, paladinName: p.raider.name,
      raider: target, blessing, reason, rebuff, source,
    });
    const have = givenTo[target.characterId] || [];
    // The single replaces THIS paladin's greater blessing on this one person,
    // so the swap has to be reflected in what they end up carrying.
    const cell = grid[cellKey(p.raider.characterId, canonClass(target.className))];
    const i = cell ? have.indexOf(cell.blessing) : -1;
    if (i >= 0) have.splice(i, 1);
    if (have.indexOf(blessing) < 0) have.push(blessing);
    givenTo[target.characterId] = have;
  };

  // Manual singles first, same as everywhere else on this sheet.
  Object.keys(manualSingles).forEach((key) => {
    const b = manualSingles[key] as BlessingKey;
    if (!b || !cat.byKey[b]) { singleSeen[key] = true; return; }
    const parts = key.split("::");
    const p = paladins.filter((x) => x.raider.characterId === parts[0])[0];
    const target = active.filter((x) => x.characterId === parts[1])[0];
    if (!p || !target) return;
    addSingle(p, target, b, "Set by hand", true, "manual");
  });

  columns.forEach((c) => {
    paladins.forEach((p) => {
      const cell = grid[cellKey(p.raider.characterId, c.className)];

      if (cell && cell.risky) {
        c.tanks.forEach((t) => {
          const swap = bestSingleFor(t, p, givenTo[t.characterId] || []);
          if (!swap) return;
          addSingle(p, t, swap,
            `${c.className}s take Salvation, and ${t.name} tanks`, true, "auto");
        });
        return;
      }

      // A paladin with no cell in this column is idle on it: five blessings is
      // the ceiling, and a column of one warrior tank only wants three. Light
      // on the tank is the best use of a spare pair of hands.
      if (!cell && c.tanks.length > 0 && p.castable.light) {
        const t = c.tanks.filter((x) => (givenTo[x.characterId] || []).indexOf("light") < 0)[0];
        if (t) {
          addSingle(p, t, "light",
            `Nothing left worth giving ${c.className}s as a group`, true, "auto");
        }
      }
    });
  });

  // 5. What is still missing. Reported rather than auto-patched: past this
  //    point every fix costs somebody a blessing they do want, and that is a
  //    call for the raid lead, not for the sheet.
  const gaps: BlessingGap[] = [];
  active.forEach((r) => {
    const have = givenTo[r.characterId] || [];
    const w = wants(cat, r);
    cat.greater.forEach((b) => {
      if (w[b] >= 8 && have.indexOf(b) < 0) gaps.push({ raider: r, blessing: b, value: w[b] });
    });
  });
  gaps.sort((a, b) => b.value - a.value || a.raider.name.localeCompare(b.raider.name));

  // 6. Warnings.
  const warnings: BlessingWarning[] = [];
  const noSanctuary = !anyCanCast("sanctuary");
  const anyTank = active.some((r) => r.role === "tank");
  if (noSanctuary && anyTank) {
    warnings.push({
      key: "no_sanctuary", tone: "warn",
      text: "No Protection paladin, so nobody has Sanctuary. Tanks get Kings and Might instead.",
    });
  }
  if (!anyCanCast("kings")) {
    warnings.push({
      key: "no_kings", tone: "warn",
      text: "Nobody here can cast Kings, which is the best blessing for almost every class. Check the talent ticks.",
    });
  }
  // A character with no recorded spec is credited only with what every spec of
  // their class has, so a paladin whose spec was never set loses Sanctuary
  // whether or not they are protection. Say so rather than let it read as a
  // talent decision.
  const guessed = paladins.filter((p) => p.inferred);
  if (guessed.length > 0) {
    warnings.push({
      key: "inferred_spec", tone: "info",
      text: `${guessed.map((p) => p.raider.name).join(", ")} ${guessed.length > 1 ? "have" : "has"} no spec recorded, so only the blessings every paladin shares are assumed. Set their spec, or tick the rest on here.`,
    });
  }
  // How many greater blessings exist is a property of the game, not a five
  // this file knows: the ceiling is however many rows sheet_blessing holds.
  const ceiling = cat.greater.length;
  if (P < ceiling) {
    const nth = ["", "1st", "2nd", "3rd", "4th", "5th", "6th", "7th"][P + 1] || `${P + 1}th`;
    warnings.push({
      key: "count", tone: "info",
      text: `${P} paladin${P > 1 ? "s" : ""}, so every class carries ${P} of the ${ceiling} blessings. A ${nth} ${P + 1 === ceiling ? "would complete the set" : "would add one more per class"}.`,
    });
  }
  const riskyCols = columns.filter((c) => paladins.some((p) => {
    const cell = grid[cellKey(p.raider.characterId, c.className)];
    return !!cell && !!cell.risky;
  }));
  if (riskyCols.length > 0) {
    warnings.push({
      key: "salv_patch", tone: "warn",
      text: `${riskyCols.map((c) => c.className + "s").join(", ")} take Salvation for the DPS in them. The tanks are patched with a single blessing, which is 10 minutes and needs recasting.`,
    });
  }

  const coverage: Record<string, BlessingKey[]> = {};
  active.forEach((r) => { coverage[r.characterId] = givenTo[r.characterId] || []; });

  return { catalogue: cat, paladins, columns, grid, singles, warnings, gaps, coverage };
}

// ── Per-player view ──────────────────────────────────────────
// Feeds the sheet's "only my duties" mode from both sides: what a paladin has
// to cast, and what everybody else should be carrying.
export type BlessingDuty = {
  kind: "cast" | "receive";
  blessing: BlessingKey;
  detail: string;
  rebuff?: boolean;
};

export function blessingDutiesFor(
  plan: BlessingPlan, characterId: string | null,
): BlessingDuty[] {
  if (!characterId) return [];
  const out: BlessingDuty[] = [];

  const isPal = plan.paladins.some((p) => p.raider.characterId === characterId);
  if (isPal) {
    // Grouped by blessing, because "Kings on eight classes" is one job and
    // eight rows of it is not a sheet, it is a wall.
    const byBlessing: Record<string, string[]> = {};
    plan.columns.forEach((c) => {
      const cell = plan.grid[cellKey(characterId, c.className)];
      if (!cell) return;
      (byBlessing[cell.blessing] = byBlessing[cell.blessing] || []).push(c.className + "s");
    });
    plan.catalogue.all.forEach((b) => {
      if (!byBlessing[b]) return;
      out.push({ kind: "cast", blessing: b, detail: byBlessing[b].join(", ") });
    });
    plan.singles.filter((s) => s.paladinId === characterId).forEach((s) => {
      out.push({
        kind: "cast", blessing: s.blessing,
        detail: `${s.raider.name} only (${s.reason})`, rebuff: s.rebuff,
      });
    });
  }

  (plan.coverage[characterId] || []).forEach((b) => {
    out.push({ kind: "receive", blessing: b, detail: shortOf(plan.catalogue, b) });
  });
  return out;
}
